import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";

import AlertBanner from "lib/components/alert/AlertBanner";
import useAlert from "lib/components/alert/useAlert";
import FeedbackButton from "lib/components/button/FeedbackButton";
import { FormField } from "lib/components/form/FormTypes";

import { asTitle, isEmailValid } from "utils/Utils";
import useFetch from "utils/fetch/useFetch";

import { useAuth } from "authentication/AuthProvider";

import OrganisationClient from "../OrganisationClient";
import {
	EditOrganisationInfo,
	OrganisationType,
	OrganisationTypeKey
} from "../OrganisationTypes";
import AddressSection from "./AddressSection";
import OrganisationInfoSection from "./OrganisationInfoSection";

type EditOrganisationGeneralFormType = {
	name: FormField<string>;
	slogan: FormField<string>;
	smallLogo: FormField<string>;
	largeLogo: FormField<string>;
	description: FormField<string>;
	type: FormField<string>;
	vatIdentification: FormField<string>;
	email: FormField<string>;
	phoneNumber: FormField<string>;
	streetName: FormField<string>;
	streetNumber: FormField<string>;
	postalCode: FormField<string>;
	city: FormField<string>;
	country: FormField<string>;
};

export type EditOrganisationGeneralFormProps = {
	id?: number;
	name: string;
	slogan: string;
	description: string;
	type: string;
	smallLogo: string;
	largeLogo: string;
	vatIdentification: string;
	streetName: string;
	streetNumber: string;
	postalCode: string;
	city: string;
	country: string;
	email: string;
	phoneNumber: string;
};

export default function EditOrganisationGeneralForm(
	props: Readonly<EditOrganisationGeneralFormProps>
): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { refreshTokens } = useAuth();

	const message = useAlert();
	const { call } = useFetch();

	const [form, setForm] = useState<EditOrganisationGeneralFormType>({
		name: { value: props.name, required: true },
		slogan: { value: props.slogan },
		smallLogo: { value: props.smallLogo },
		largeLogo: { value: props.largeLogo },
		description: { value: props.description },
		type: { value: props.type, required: true },
		vatIdentification: { value: props.vatIdentification },
		email: { value: props.email, required: true },
		phoneNumber: { value: props.phoneNumber, required: true },
		streetName: { value: props.streetName, required: true },
		streetNumber: { value: props.streetNumber, required: true },
		postalCode: { value: props.postalCode, required: true },
		city: { value: props.city, required: true },
		country: { value: props.country, required: true }
	});

	function validate(): boolean {
		form.name.error = form.name.value.trim() === "";
		form.type.error =
			form.type.value.trim() === "" ||
			Object.values(OrganisationType).every(
				key => key !== form.type.value
			);
		form.email.error = !isEmailValid(form.email.value);
		form.phoneNumber.error = form.phoneNumber.value.trim() === "";
		form.streetName.error = form.streetName.value.trim() === "";
		form.streetNumber.error = form.streetNumber.value.trim() === "";
		form.postalCode.error = form.postalCode.value.trim() === "";
		form.city.error = form.city.value.trim() === "";
		form.country.error = form.country.value.trim() === "";

		return Object.values(form).every(field => field.error !== true);
	}

	const onSave = useCallback((): Promise<void> => {
		message.clear();

		const valid = validate();
		if (!valid) {
			setForm({ ...form });
			return Promise.resolve();
		}

		const data: EditOrganisationInfo = {
			name: form.name.value,
			slogan: form.slogan.value,
			description: form.description.value,
			type: form.type.value as OrganisationTypeKey,
			smallLogo: form.smallLogo.value,
			largeLogo: form.largeLogo.value,
			vatIdentification: form.vatIdentification.value,
			streetName: form.streetName.value,
			streetNumber: form.streetNumber.value,
			postalCode: form.postalCode.value,
			city: form.city.value,
			country: form.country.value,
			email: form.email.value,
			phoneNumber: form.phoneNumber.value
		};

		if (props.id) {
			return call(OrganisationClient.update(props.id, data))
				.then(() => {
					message.success(
						t("saveDocumentSuccess", {
							document: "organisationInfo"
						})
					);
				})
				.catch(() => {
					message.error(
						t("saveDocumentFailure", {
							document: "organisationInfo"
						})
					);
				});
		} else {
			return call(OrganisationClient.create(data))
				.then(responseData => refreshTokens(responseData.id))
				.then(() => navigate("/"))
				.catch(() => {
					message.error(
						t("saveDocumentFailure", {
							document: "organisationInfo"
						})
					);
				});
		}
	}, [form]);

	const onCancel = useCallback((): void => {
		props.id ? navigate("/") : navigate(-1);
	}, []);

	return (
		<Grid container direction="column" rowSpacing={2}>
			<Grid>{<AlertBanner alert={message.alert} />}</Grid>
			<Grid>
				<OrganisationInfoSection
					name={form.name}
					slogan={form.slogan}
					description={form.description}
					smallLogo={form.smallLogo}
					largeLogo={form.largeLogo}
					type={form.type}
					vatIdentification={form.vatIdentification}
					email={form.email}
					phoneNumber={form.phoneNumber}
				/>
			</Grid>

			<Grid>
				<AddressSection
					streetName={form.streetName}
					streetNumber={form.streetNumber}
					postalCode={form.postalCode}
					city={form.city}
					country={form.country}
				/>
			</Grid>

			<Grid container columnSpacing={2}>
				<Grid>
					<FeedbackButton
						label={asTitle(t(props.id ? "save" : "create"))}
						variant="contained"
						onClick={onSave}
					/>
				</Grid>
				<Grid>
					<Button variant="outlined" onClick={onCancel}>
						{asTitle(t("cancel"))}
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
}
